import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { NotificationType } from 'angular2-notifications'
import { from, of } from 'rxjs'
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators'
import { AppNotificationService } from '../../services/app-notification.service'
import { AppSpinnerService } from '../../services/app-spinner.service'
import { AppCoreService } from '../../services/app-core.service'
import { Doodle, DoodleFilters, DoodleListResponse } from '../../../features/feature-doodles/models/interfaces/doodle.interface'
import { DoodleListParams } from '../../../features/feature-doodles/models/interfaces/doodle-list-params.interface'
import { MailingList } from '../../../features/feature-mailing-list/models/mailing-list.interface'
import { AppBasicCalendarEvent } from '../../models/interfaces/app-basic-calendar-event.interface'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import { AppState } from '../storage'
import * as CalendarEventsActions from './calendar-events.actions'
import { selectDoodleFilters, selectDoodlesParams } from './calendar-events.selectors'
import { CalendarEventsService } from './calendar-events.service'

@Injectable()
export class CalendarEventsEffects {
   loadMlistList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.loadMailingList),
         switchMap((action) =>
            this.calendarEventsService.getMlistList().pipe(
               map((response: MailingList[]) => CalendarEventsActions.loadMailingListSuccess({ response })),
               catchError((error) => of(CalendarEventsActions.loadMailingListFailed({ error })))
            )
         )
      )
   )

   loadMlist$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.loadSingleMailingList),
         switchMap((action) =>
            this.calendarEventsService.getMlistById(action.id).pipe(
               map((response: MailingList) => CalendarEventsActions.loadSingleMailingListSuccess({ response })),
               catchError((error) => of(CalendarEventsActions.loadSingleMailingListFailed({ error })))
            )
         )
      )
   )

   addMlist$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.addMailingList),
         switchMap((action) =>
            this.calendarEventsService.insertMlist(action.data).pipe(
               map(() => CalendarEventsActions.addMailingListSuccess()),
               catchError((error) => of(CalendarEventsActions.addMailingListFailed({ error })))
            )
         )
      )
   )

   updateMlist$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.updateMailingList),
         switchMap((action) =>
            this.calendarEventsService.updateMlist(action.id, action.data).pipe(
               map(() => CalendarEventsActions.updateMailingListSuccess()),
               catchError((error) => of(CalendarEventsActions.updateMailingListFailed({ error })))
            )
         )
      )
   )

   deleteMlist$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.deleteMailingList),
         switchMap((action) =>
            this.calendarEventsService.deleteMlist(action.id).pipe(
               map(() => CalendarEventsActions.deleteMailingListSuccess()),
               catchError((error) => of(CalendarEventsActions.deleteMailingListFailed({ error })))
            )
         )
      )
   )

   reloadMailingListsAfterMutation$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.addMailingListSuccess, CalendarEventsActions.updateMailingListSuccess, CalendarEventsActions.deleteMailingListSuccess),
         map(() => CalendarEventsActions.loadMailingList())
      )
   )

   loadEventList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.loadEventList),
         mergeMap((action) =>
            this.calendarEventsService.getEventList(action?.modePreview).pipe(
               map((response: AppBasicCalendarEvent[]) => CalendarEventsActions.loadEventListSuccess({ response, modePreview: action.modePreview })),
               catchError((error) => of(CalendarEventsActions.loadEventListFailed({ error, modePreview: action.modePreview })))
            )
         )
      )
   )

   loadEvent$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.loadSingleEvent),
         switchMap((action) =>
            this.calendarEventsService.getEventById(action.id).pipe(
               map((response: AppBasicCalendarEvent) => CalendarEventsActions.loadSingleEventSuccess({ response })),
               catchError((error) => of(CalendarEventsActions.loadSingleEventFailed({ error })))
            )
         )
      )
   )

   addEvent$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.addEvent),
         switchMap((action) =>
            this.calendarEventsService.insertEvent(action.data).pipe(
               map(() => CalendarEventsActions.addEventSuccess()),
               catchError((error) => of(CalendarEventsActions.addEventFailed({ error })))
            )
         )
      )
   )

   updateEvent$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.updateEvent),
         switchMap((action) =>
            this.calendarEventsService.updateEvent(action.id, action.data).pipe(
               map(() => CalendarEventsActions.updateEventSuccess({ start: action.data.start })),
               catchError((error) => of(CalendarEventsActions.updateEventFailed({ error })))
            )
         )
      )
   )

   deleteEvent$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.deleteEvent),
         switchMap((action) =>
            this.calendarEventsService.deleteEvent(action.id).pipe(
               map(() => CalendarEventsActions.deleteEventSuccess()),
               catchError((error) => of(CalendarEventsActions.deleteEventFailed({ error })))
            )
         )
      )
   )

   reloadEventListsAfterMutation$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.addEventSuccess, CalendarEventsActions.updateEventSuccess, CalendarEventsActions.deleteEventSuccess),
         switchMap(() => from([CalendarEventsActions.loadEventList({}), CalendarEventsActions.loadEventList({ modePreview: true })]))
      )
   )

   loadDoodleList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.loadDoodleList),
         withLatestFrom(this.store.pipe(select(selectDoodleFilters))),
         switchMap(([action, filters]: [{ params: DoodleListParams }, DoodleFilters | undefined]) =>
            this.calendarEventsService.getDoodleList(action.params, filters).pipe(
               map((response: DoodleListResponse) => CalendarEventsActions.loadDoodleListSuccess({ response })),
               catchError((error) => of(CalendarEventsActions.loadDoodleListFailed({ error })))
            )
         )
      )
   )

   reloadDoodleListAfterMutation$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.addDoodleSuccess, CalendarEventsActions.updateDoodleSuccess, CalendarEventsActions.deleteDoodleSuccess),
         withLatestFrom(this.store.pipe(select(selectDoodlesParams))),
         map(([, params]) => CalendarEventsActions.loadDoodleList({ params: params ?? { page: 0, pageSize: 10 } }))
      )
   )

   loadDoodle$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.loadDoodle),
         switchMap((action) =>
            this.calendarEventsService.getDoodleById(action.id).pipe(
               map((response: Doodle) => CalendarEventsActions.loadDoodleSuccess({ response })),
               catchError((error) => of(CalendarEventsActions.loadDoodleFailed({ error })))
            )
         )
      )
   )

   addDoodle$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.addDoodle),
         switchMap((action) =>
            this.calendarEventsService.insertDoodle(action.data).pipe(
               map(() => CalendarEventsActions.addDoodleSuccess()),
               catchError((error) => of(CalendarEventsActions.addDoodleFailed({ error })))
            )
         )
      )
   )

   updateDoodle$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.updateDoodle),
         switchMap((action) =>
            this.calendarEventsService.updateDoodle(action.data).pipe(
               map(() => CalendarEventsActions.updateDoodleSuccess()),
               catchError((error) => of(CalendarEventsActions.updateDoodleFailed({ error })))
            )
         )
      )
   )

   deleteDoodle$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.deleteDoodle),
         switchMap((action) =>
            this.calendarEventsService.deleteDoodle(action.id).pipe(
               map(() => CalendarEventsActions.deleteDoodleSuccess()),
               catchError((error) => of(CalendarEventsActions.deleteDoodleFailed({ error })))
            )
         )
      )
   )

   voteDoodle$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.voteDoodle),
         switchMap((action) =>
            this.calendarEventsService.voteDoodle(action.data).pipe(
               map(() => CalendarEventsActions.voteDoodleSuccess()),
               catchError((error) => of(CalendarEventsActions.voteDoodleFailed({ error })))
            )
         )
      )
   )

   approveDoodle$ = createEffect(() =>
      this.actions$.pipe(
         ofType(CalendarEventsActions.approveDoodle),
         switchMap((action) =>
            this.calendarEventsService.approveDoodle(action.data).pipe(
               map(() => CalendarEventsActions.approveDoodleSuccess()),
               catchError((error) => of(CalendarEventsActions.approveDoodleFailed({ error })))
            )
         )
      )
   )

   constructor(
      private store: Store<AppState>,
      private actions$: Actions,
      private calendarEventsService: CalendarEventsService,
      private coreService: AppCoreService,
      private spinnerService: AppSpinnerService,
      private notificationService: AppNotificationService,
      private errorHandlerService: AppErrorHandlerService
   ) {}

   private _actionsForSpinnerHide = [
      CalendarEventsActions.addMailingListSuccess,
      CalendarEventsActions.updateMailingListSuccess,
      CalendarEventsActions.deleteMailingListSuccess,
      CalendarEventsActions.addEventSuccess,
      CalendarEventsActions.updateEventSuccess,
      CalendarEventsActions.deleteEventSuccess,
      CalendarEventsActions.addDoodleSuccess,
      CalendarEventsActions.updateDoodleSuccess,
      CalendarEventsActions.deleteDoodleSuccess,
      CalendarEventsActions.voteDoodleSuccess,
      CalendarEventsActions.approveDoodleSuccess,
      CalendarEventsActions.deleteMailingListFailed,
      CalendarEventsActions.deleteEventFailed,
      CalendarEventsActions.deleteDoodleFailed,
      CalendarEventsActions.addMailingListFailed,
      CalendarEventsActions.updateMailingListFailed,
      CalendarEventsActions.addEventFailed,
      CalendarEventsActions.updateEventFailed,
      CalendarEventsActions.addDoodleFailed,
      CalendarEventsActions.updateDoodleFailed,
      CalendarEventsActions.voteDoodleFailed,
      CalendarEventsActions.approveDoodleFailed,
      CalendarEventsActions.loadMailingListSuccess,
      CalendarEventsActions.loadMailingListFailed,
      CalendarEventsActions.loadEventListSuccess,
      CalendarEventsActions.loadEventListFailed,
      CalendarEventsActions.loadDoodleListSuccess,
      CalendarEventsActions.loadDoodleListFailed
   ]

   listenAllSpinnerHideAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerHide),
            map(() => this.spinnerService.spinnerHide())
         ),
      { dispatch: false }
   )

   private _actionsForSpinnerShow = [
      CalendarEventsActions.addMailingList,
      CalendarEventsActions.updateMailingList,
      CalendarEventsActions.deleteMailingList,
      CalendarEventsActions.addEvent,
      CalendarEventsActions.updateEvent,
      CalendarEventsActions.deleteEvent,
      CalendarEventsActions.addDoodle,
      CalendarEventsActions.updateDoodle,
      CalendarEventsActions.deleteDoodle,
      CalendarEventsActions.voteDoodle,
      CalendarEventsActions.approveDoodle,
      CalendarEventsActions.loadMailingList,
      CalendarEventsActions.loadDoodleList
   ]

   listenAllSpinnerShowAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerShow),
            map(() => this.spinnerService.spinnerShow())
         ),
      { dispatch: false }
   )

   private _actionsFailedNotification = [
      CalendarEventsActions.loadMailingListFailed,
      CalendarEventsActions.loadEventListFailed,
      CalendarEventsActions.loadDoodleListFailed,
      CalendarEventsActions.deleteMailingListFailed,
      CalendarEventsActions.deleteEventFailed,
      CalendarEventsActions.deleteDoodleFailed,
      CalendarEventsActions.addMailingListFailed,
      CalendarEventsActions.updateMailingListFailed,
      CalendarEventsActions.addEventFailed,
      CalendarEventsActions.updateEventFailed,
      CalendarEventsActions.addDoodleFailed,
      CalendarEventsActions.updateDoodleFailed,
      CalendarEventsActions.voteDoodleFailed,
      CalendarEventsActions.approveDoodleFailed,
      CalendarEventsActions.loadSingleMailingListFailed,
      CalendarEventsActions.loadSingleEventFailed,
      CalendarEventsActions.loadDoodleFailed
   ]

   listenAllFailedAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsFailedNotification),
            map((errorResponse) => {
               this.errorHandlerService.handleError(errorResponse.error)
            })
         ),
      { dispatch: false }
   )

   private _actionsSuccessNotification = [
      CalendarEventsActions.addMailingListSuccess,
      CalendarEventsActions.updateMailingListSuccess,
      CalendarEventsActions.deleteMailingListSuccess,
      CalendarEventsActions.addEventSuccess,
      CalendarEventsActions.updateEventSuccess,
      CalendarEventsActions.deleteEventSuccess,
      CalendarEventsActions.addDoodleSuccess,
      CalendarEventsActions.updateDoodleSuccess,
      CalendarEventsActions.deleteDoodleSuccess
   ]

   listenAllSuccessAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsSuccessNotification),
            map(() => {
               this.notificationService.notify(NotificationType.Success)
               this.coreService.closeDialog()
            })
         ),
      { dispatch: false }
   )
}
